import {
  ErrorDto,
  ErrorDtoCodeEnum,
  EtatDtoEtatEnum,
} from 'lib_api/lib/api/gen';

import { BaseOptionType } from 'components/BaseForm/Select/BaseSelect';

export function getOptionValueAsStringOrNull<T>(option?: BaseOptionType<T>) {
  if (!option) {
    return null;
  }
  return getStringOrNullOrThrow(option.value);
}

export function getStringOrNullOrThrow(toParse: unknown): string | null {
  assertIsStringOrNull(toParse);
  return toParse;
}

function assertIsStringOrNull(value: unknown): asserts value is string | null {
  if (typeof value !== 'string' && value !== null) {
    throw new TypeError('Value is not of the expected type');
  }
  return;
}

export function getStringOrThrow(toParse: unknown): string {
  assertIsString(toParse);
  return toParse;
}

function assertIsString(value: unknown): asserts value is string {
  if (typeof value !== 'string') {
    throw new TypeError('Value is not of the expected type');
  }
  return;
}

export function getFunctionOrThrow(toParse: unknown) {
  assertIsFunction(toParse);
  return toParse;
}

function assertIsFunction<T extends (...args: unknown[]) => unknown>(
  value: unknown,
): asserts value is T {
  if (typeof value !== 'function') {
    throw new Error('Value is not a function');
  }
  return;
}

export function getBlobOrThrow(toParse: unknown): Blob {
  assertIsBlob(toParse);
  return toParse;
}

function assertIsBlob(value: unknown): asserts value is Blob {
  if (!(value instanceof Blob)) {
    throw new TypeError('Value is not a Blob');
  }
  return;
}

export function unsafeCast<T>(value: unknown): T {
  // This function is a placeholder to cast any type to 'any'.
  // It can be used when you need to bypass TypeScript's strict type checking.
  return value as T;
}

export function getFileOrUndefinedOrThrow(toParse: unknown): File | undefined {
  assertIsFileOrUndefined(toParse);
  return toParse;
}

function assertIsFileOrUndefined(
  value: unknown,
): asserts value is File | undefined {
  if (value === undefined || value instanceof File) {
    return;
  }
  throw new TypeError('Value is not of the expected type');
}

export function getArrayOrUndefinedOrThrow(
  toParse: unknown,
): unknown[] | undefined {
  assertIsArrayOrUndefined(toParse);
  return toParse;
}

function assertIsArrayOrUndefined(
  value: unknown,
): asserts value is unknown[] | undefined {
  if (value === undefined || Array.isArray(value)) {
    return;
  }
  throw new TypeError('Value is not of the expected type');
}

export function getBooleanOrUndefinedOrThrow(
  toParse: unknown,
): boolean | undefined {
  assertIsBooleanOrUndefined(toParse);
  return toParse;
}

function assertIsBooleanOrUndefined(
  value: unknown,
): asserts value is boolean | undefined {
  if (value === undefined || typeof value === 'boolean') {
    return;
  }
  throw new TypeError('Value is not of the expected type');
}

export function getEtatDtoEtatEnumOrThrow(toParse: unknown): EtatDtoEtatEnum {
  assertIsEtatDtoEtatEnum(toParse);
  return toParse;
}

function assertIsEtatDtoEtatEnum(
  value: unknown,
): asserts value is EtatDtoEtatEnum {
  if (isEtatDtoEtatEnum(value)) {
    return;
  }
  throw new TypeError('Value is not of the expected type');
}

function isEtatDtoEtatEnum(value: unknown): value is EtatDtoEtatEnum {
  if (typeof value !== 'string') {
    return false;
  }
  return Object.values(EtatDtoEtatEnum).includes(value as EtatDtoEtatEnum);
}

function assertIsErrorDtoCodeEnum(
  value: unknown,
): asserts value is ErrorDtoCodeEnum {
  // Check 1: Ensure the value is a string, as ErrorDtoCodeEnum is a string enum.
  if (typeof value !== 'string') {
    throw new TypeError(
      `Invalid error code: Expected a string, but received type '${typeof value}'.`,
    );
  }

  // Check 2: Get all valid string values from the enum.
  const validCodes = Object.values(ErrorDtoCodeEnum);

  // Check 3: Check if the string value is present in the list of valid codes.
  if (!validCodes.includes(value as ErrorDtoCodeEnum)) {
    throw new TypeError(
      `Invalid ErrorDtoCodeEnum value: '${value}'. ` +
        `Must be one of the known error codes defined in the enum.`,
    );
  }
}

export function getErrorDtoOrThrow(toParse: unknown): ErrorDto {
  assertIsErrorDto(toParse);
  return toParse;
}

function assertIsErrorDto(toParse: unknown): asserts toParse is ErrorDto {
  assertIsPlainObject(toParse);
  assertIsErrorDtoCodeEnum(toParse.code);
  getStringOrThrow(toParse.message);
  assertIsStringOrNull(toParse.localized);

  return;
}

function assertIsPlainObject(
  value: unknown,
): asserts value is Record<string, unknown> {
  if (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Function)
  ) {
    return;
  }
  throw new TypeError();
}

const parseNumberFromString = (stringNumber: string): number => {
  const parsedNumber = Number(stringNumber);
  if (isNaN(parsedNumber)) {
    throw new TypeError(`Value cannot be parsed to a number: ${stringNumber}`);
  }
  return parsedNumber;
};

export const getUnknowNumberOrThrow = (
  unknowNumber: unknown,
): number | undefined => {
  if (unknowNumber === undefined) {
    return undefined;
  }
  switch (typeof unknowNumber) {
    case 'number':
      return unknowNumber;
    case 'string':
      if (!unknowNumber.trim()) {
        return undefined;
      }
      return parseNumberFromString(unknowNumber);
    default:
      throw new TypeError(
        `Value is not of the expected type : ${typeof unknowNumber}`,
      );
  }
};
